import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { snakeCase } from 'lodash';

import {
  ButtonModel,
  IconSolidInfo,
  RadioButton,
  Spinner,
  Text,
} from '@notino/react-styleguide';
import {
  GetCatalogProductViewQuery,
  StockAvailability,
} from '@notino/shared/definitions/types';
import { CartType } from '@notino/web-tracking';

import { usePriceLogic } from '@containers/ProductDetailContainer/ProductDetail/hooks/usePriceLogic';
import {
  MIN_PRICE_PROMO_LABEL,
  RRP_PRICE_PROMO_LABEL,
} from '@containers/ProductDetailContainer/ProductDetail/hooks/usePriceLogic/tracking';
import { useProductDetailContext } from '@containers/ProductDetailContainer/ProductDetailContext';
import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';
import { dispatchTrackingEvent } from '@context/tracking/utils';
import { ProductEventWither } from '@helpers/googleTagManager';
import { getGiftCampaigns } from '@lib/gift';

import { getRenderableDiscoBoxSampleId } from '../../DiscoveryBoxSelfChoice';

import { Store } from './components/Store';
import { useTrackModalOrDrawerShow } from './hooks/useTrackModalShow';
import { messages } from './messages';
import {
  ContentWrapper,
  StoreWrapper,
  CenterWrapper,
  StyledActionButtonWithConfirmation,
  ActionButtonWithConfirmationWrapper,
  GiftInformationBox,
} from './styled';
import { useClickAndCollect } from './useClickAndCollect';

type Product = NonNullable<
  GetCatalogProductViewQuery['productDetailByCatalogMasterId']
>;

interface IClickAndCollectContentProps {
  product: Product;
  selectedVariant: Product['variants'][number];
}

export const ClickAndCollectContent = ({
  selectedVariant,
  product,
}: IClickAndCollectContentProps) => {
  const [selectedStore, setSelectedStore] = React.useState('');
  const { rrpShown, minimalPriceShown } = usePriceLogic(selectedVariant);
  const flags = useFeatureFlags();

  const { giftCampaigns } = getGiftCampaigns({
    product,
    variant: selectedVariant,
  });

  const shouldShowGiftInformationBox = giftCampaigns?.length > 0;

  const { tryItFirstAvailable, engravingAvailable } = useProductDetailContext();

  const {
    stores,
    storesLoading,
    handleAddToClickAndCollect,
    addToClickAndCollectData: {
      loading: addToCnCLoading,
      error: addToCnCError,
    },
  } = useClickAndCollect(selectedVariant);

  useTrackModalOrDrawerShow();

  if (storesLoading) {
    return (
      <CenterWrapper tabIndex={0}>
        <Spinner color="icon.primary" size={32} />
      </CenterWrapper>
    );
  }

  const onRadioButtonClick = (storeId: string) => () => {
    setSelectedStore(storeId);
  };

  const handleSubmit = async () => {
    dispatchTrackingEvent({
      event: 'add_to_cart',
      product: ProductEventWither()
        .withProduct(product)
        .withVariant(selectedVariant)
        .withDiscountPrices(selectedVariant.attributes)
        .withServices({
          tryItFirstAvailable: tryItFirstAvailable!,
          engravingAvailable: engravingAvailable!,
          discoveryBoxAvailable: Boolean(
            getRenderableDiscoBoxSampleId(
              product,
              selectedVariant,
              flags as Pick<typeof flags, 'discovery_box_self_choice_enabled'>
            )
          ),
        })
        .withAdditionalData({
          quantity: 1,
          cart_type: snakeCase(CartType.clickAndCollect),
        })
        .withAdditionalPromoLabels(
          [
            rrpShown && RRP_PRICE_PROMO_LABEL,
            minimalPriceShown && MIN_PRICE_PROMO_LABEL,
          ].filter((v): v is string => typeof v === 'string')
        )
        .build(),
      _clear: true,
    });

    await handleAddToClickAndCollect(selectedStore);
  };

  return (
    <>
      <ContentWrapper
        shouldShowGiftInformationBox={shouldShowGiftInformationBox}
      >
        {stores?.collectStoresByCatalogId?.map((store) => {
          const outOfStock = store.stockCount === StockAvailability.OutOfStock;
          if (outOfStock) {
            return null;
          }

          return (
            <StoreWrapper key={store.code}>
              <RadioButton
                onChange={onRadioButtonClick(store.code)}
                position={4}
                disabled={store.isOverloaded ?? false}
                checked={selectedStore === store.code}
                labelDataTestId="collectStoreItem"
              >
                <Store data={store} />
              </RadioButton>
            </StoreWrapper>
          );
        })}
      </ContentWrapper>

      <ActionButtonWithConfirmationWrapper>
        {shouldShowGiftInformationBox && (
          <GiftInformationBox>
            <IconSolidInfo color="icon.secondary" height="16px" width="16px" />
            <Text typography="labelSmall400">
              <FormattedMessage {...messages.pickUpGiftInformation} />
            </Text>
          </GiftInformationBox>
        )}

        <StyledActionButtonWithConfirmation
          buttonStyle={ButtonModel.Styles.primary}
          fullWidth={true}
          onClick={handleSubmit}
          isLoading={addToCnCLoading}
          error={!!addToCnCError}
          data-testid="pickHereButton"
          disabled={!selectedStore}
        >
          <FormattedMessage {...messages.pickHere} />
        </StyledActionButtonWithConfirmation>
      </ActionButtonWithConfirmationWrapper>
    </>
  );
};
